/**
 * Säljare från en lista med namn: hur listan läses, säljkoderna och
 * lösenorden på inloggningslapparna. Delas av API:t (som skapar kontona)
 * och appen (som visar kontrollen innan något skapas), så att båda läser
 * listan på samma sätt.
 */

/** Högst så många namn per gång i appen. */
export const MAX_SELLERS_PER_LIST = 60;
/** Högst så många per anrop till servern; appen skickar listan i omgångar. */
export const MAX_SELLERS_PER_REQUEST = 10;
export const MAX_NAME_LENGTH = 80;

export type ListRowStatus = 'new' | 'duplicate' | 'existing';

export interface ListRow {
    name: string;
    /**
     * new: skapas. duplicate: står redan högre upp i listan, hoppas över.
     * existing: liknar en säljare som redan finns i gruppen (samma förnamn och
     * samma initial i efternamnet). Hoppas över, om den inte väljs ändå.
     */
    status: ListRowStatus;
    /** existing: säljarna i gruppen som namnet liknar. */
    matches?: string[];
    /** existing: valt att skapas ändå (t.ex. två barn med samma förnamn). */
    include?: boolean;
}

export interface ParsedList {
    rows: ListRow[];
    /** Namnen som ska skapas, i listans ordning. */
    toCreate: string[];
    blankLines: number;
}

/** Ett namn som det sparas: enkla mellanslag, inga styrtecken, högst 80 tecken. */
export const cleanName = (raw: string): string =>
    raw.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_LENGTH).trim();

const nameKey = (name: string) => cleanName(name).toLocaleLowerCase('sv-SE');

/** Förnamnet och första bokstaven i efternamnet: "Elias Fallgren" och "Elias F." → elias, f. */
export const nameParts = (name: string): { first: string; initial: string } => {
    const words = cleanName(name).toLocaleLowerCase('sv-SE').split(' ').map(w => w.replace(/[^\p{L}\p{N}-]/gu, '')).filter(Boolean);
    return { first: words[0] || '', initial: (words[1] || '')[0] || '' };
};

/**
 * Troligen samma person: samma förnamn och samma första bokstav i
 * efternamnet, eller samma förnamn där det ena saknar efternamn.
 * "Elias Fallgren" och "Elias F" liknar varandra, "Elias F" och "Elias B" inte.
 */
export const similarNames = (a: string, b: string): boolean => {
    const x = nameParts(a);
    const y = nameParts(b);
    if (!x.first || x.first !== y.first) return false;
    return x.initial === y.initial || !x.initial || !y.initial;
};

/**
 * Läser en inklistrad lista: ett namn per rad, tomma rader och dubbletter tas
 * bort. Namn som liknar en säljare som redan finns i gruppen hoppas över, om
 * de inte finns i `include` (namnen som valts att skapas ändå).
 */
export const parseSellerList = (text: string, existingNames: string[] = [], include: Iterable<string> = []): ParsedList => {
    const chosen = new Set([...include].map(nameKey));
    const seen = new Set<string>();
    const rows: ListRow[] = [];
    let blankLines = 0;
    for (const line of text.split(/\r?\n/)) {
        const name = cleanName(line);
        if (!name) { blankLines++; continue; }
        const key = nameKey(name);
        if (seen.has(key)) { rows.push({ name, status: 'duplicate' }); continue; }
        seen.add(key);
        const matches = existingNames.filter(existing => similarNames(name, existing));
        rows.push(matches.length > 0 ? { name, status: 'existing', matches, include: chosen.has(key) } : { name, status: 'new' });
    }
    return {
        rows,
        toCreate: rows.filter(r => r.status === 'new' || (r.status === 'existing' && r.include)).map(r => r.name),
        blankLines,
    };
};

/** Början på säljkoden: namnet utan accenter och tecken, "Åsa Ö." → "asao". */
export const slugBase = (name: string): string => {
    const base = name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '').slice(0, 20);
    return base || 'saljare';
};

/** Utan tecken som är lätta att blanda ihop på en lapp (0/o, 1/l/i). */
export const CODE_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** Slumptal 0 ≤ n < max. Servern skickar in node:crypto randomInt. */
export type RandomInt = (max: number) => number;

export const sellerSlug = (name: string, randomInt: RandomInt): string => {
    let suffix = '';
    for (let i = 0; i < 4; i++) suffix += CODE_ALPHABET[randomInt(CODE_ALPHABET.length)];
    return `${slugBase(name)}-${suffix}`;
};

/** Ord för lösenorden på lapparna: korta, svenska, utan å ä ö. */
export const PASSWORD_WORDS = [
    'mocka', 'latte', 'kakao', 'bona', 'rosta', 'kanel', 'fika', 'krema',
    'kopp', 'mjolk', 'socker', 'bulle', 'kaka', 'vanilj', 'kardemumma', 'espresso',
    'cortado', 'filter', 'kvarn', 'termos', 'kola', 'mandel', 'honung', 'skum',
];

/** Ett kaffeord och fyra siffror, t.ex. "mocka-4821". Minst nio tecken. */
export const sellerPassword = (randomInt: RandomInt): string => {
    const word = PASSWORD_WORDS[randomInt(PASSWORD_WORDS.length)];
    const digits = String(randomInt(10000)).padStart(4, '0');
    return `${word}-${digits}`;
};

/** Det som står på en inloggningslapp. */
export interface SellerCredentials {
    id: string;
    name: string;
    slug: string;
    password: string;
}
